import React, { type ChangeEvent, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, Error, Button, Label, Icon, IconButton, type ITooltipProps } from 'shared/ui'
import { Typography } from 'shared/ui/Typography'
import { getAriaLabel } from 'shared/lib'

import styles from './styles.module.scss'

type IImageUploaderInputProps = {
  setFile: (file: File) => void
  url: string
  hasUrl: boolean
  label: string
  subtitle: string
  loading: boolean
  remove: () => void
  error: string
  isSquare?: boolean
  tooltipProps?: ITooltipProps
}

export const ImageUploaderInput: React.FC<IImageUploaderInputProps> = observer(
  ({ setFile, url, hasUrl, label, subtitle, loading, remove, error, isSquare, tooltipProps }) => {
    const ref = useRef<HTMLInputElement>(null)

    const handleUploadClick = () => {
      if (ref.current) {
        ref.current.value = ''
        ref.current.click()
      }
    }

    const onUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]

      if (file) {
        setFile(file)
      }
    }

    return (
      <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <div className={styles.logoWrap}>
            <div
              className={classNames(styles.logo, { [styles.square]: isSquare })}
              style={{ backgroundImage: `url(${url})` }}
            >
              {!hasUrl && (
                <Icon icon={'imageFilled'} color='var(--content-neutral-primary)' fontSize={16} />
              )}
            </div>
            <div className={styles.labelWrap}>
              <div className={styles.label}>
                <Label
                  label={{
                    text: label,
                    textColor: 'var(--content-primary-primary)',
                    horizontal: true,
                  }}
                  questionTooltipProps={{
                    label: label,
                    placement: 'top',
                    ...tooltipProps,
                  }}
                />
              </div>
              {error ? (
                <Error type='text' error={error} />
              ) : (
                <Typography
                  ariaLabel={getAriaLabel('LogoMaxSize')}
                  variant={'body-md-regular'}
                  color='var(--content-primary-secondary)'
                  className={styles.logoSubtitle}
                  ellipsis
                >
                  {subtitle}
                </Typography>
              )}
            </div>
          </div>
          {!hasUrl ? (
            <Button
              text='Upload'
              icon='upload'
              size='medium'
              contained='secondary'
              onClick={handleUploadClick}
              className={styles.uploadButton}
              loading={loading}
            />
          ) : (
            <IconButton
              ariaLabel={getAriaLabel('RemoveRcsLogo')}
              icon='close'
              size='medium'
              variant='icon'
              color='tertiary'
              className={styles.removeButton}
              tooltipProps={{
                label: 'Remove',
                placement: 'top',
              }}
              loading={loading}
              onClick={remove}
            />
          )}
          <input
            hidden
            type={'file'}
            ref={ref}
            accept={
              'image/jpeg, image/png, image/jpg, image/gif, image/bmp, image/tiff, image/webp, image/heic, image/heif'
            }
            onChange={onUploadChange}
          />
        </Box>
      </Box>
    )
  }
)
